import _ from './underscore';
import Logger from './logger';
import {namedSetInterval, namedSetTimeout} from '../../../common/utils/time-functions';
import {TimeIntervalName} from '../../../common/utils/time-interval-names';

export interface TimeoutSubscription {
    clear(): void;
}



export interface ElementDimensions {

    width: number,
    height: number,
    horizontMargin: number,
    verticalMargin: number,
    horizontPadding: number,
    verticalPadding: number,
    horizontBorder: number,
    verticalBorder: number,
    fullWidth: number,
    fullHeight: number,
    marginRight: number,
    marginLeft: number,
    marginTop: number,
    marginBottom: number
}

export interface ElementSize {
    width: number,
    height: number
}

export interface DomEventSubscription {
    remove: () => void
}



class DOM {

    static CLASS = 'DOM';

    constructor() {
        this._createLogger();
    }

    logger: Logger;
    _createLogger() {
        this.logger = new Logger(DOM.CLASS);
    }


    getBody() : HTMLElement {
        return document.body;
    }


    getHTML(): HTMLElement {
        return document.documentElement;
    }


    /**
     * Draw element into the DOM.
     */
    draw(element: Element) : DOM {

        this.append(this.getBody(), element);

        return this;
    }


    /**
     * Selector jQuery like for getting the object from the dom.
     * $$ is double the fun, $ is cool, $$ is cooler.
     */
    $$(query: any): any {
        let el = null;

        // first off if it's not a string assume it's already an element
        if (!_.isString(query)) {
            el = query;
        } else if ( this.isTag(query) ) {
            // check if it's a tag
            let tag = query.substring(1, query.length - 1);
            el = document.createElement( tag );
        } else if ( this.isId(query) ) {
            // check if it's an id
            let id = query.substr(1);
            el = document.getElementById( id );
        } else {
            // run a basic query
            el = document.querySelectorAll(query);
        }

        if (_.isNodeList(el)) {
            el = [].slice.call(el);
        }

        return el;
    }

    querySelector(selector: string): Element {
        return document.querySelector(selector);
    }


    injectStyleToDOM(content: string): HTMLStyleElement {
        let newStyle = document.createElement('style');
        newStyle.innerHTML = content;
        return document.body.appendChild(newStyle);
    }

    createElement(type: string = 'div', html: string = '', classList = []): HTMLElement {
        let domElement = document.createElement(type);
        if(html) {
            domElement.innerHTML = html;
        }

        if(classList.length > 0){
            classList.forEach(_class => this.addClass(domElement, _class));
        }

        return domElement;
    }

    append(target: Element, el: Element) : DOM {
        if (!target || !_.isElement(target)) {
            return this;
        }

        if (!el || !_.isElement(el)) {
            return this;
        }

        target.appendChild(el);

        return this;
    }



    removeElement(target: Element, el: Element) : DOM {
        if (!target || !_.isElement(target)) {
            return this;
        }

        if (!el || !_.isElement(el)) {
            return this;
        }
        el.parentNode.removeChild(el);

        return this;
    }

    appendFirst(target: Element, el: Element): void {

        if (!_.isElement(target)) {
            throw new Error('target argument must be an Element');
        }

        if (!_.isElement(el)) {
            throw new Error('el argument must be an Element');
        }

        target.insertBefore(el, target.firstChild);
    }

    addClass(el: Element, className: any) : DOM {
        if (!_.isElement(el)) {
            return this;
        }

        if (!className || 0 === className.length) {
            return this;
        }

        let name = _.isArray(className) ? className[0] : className;

        if (el.classList) {
            el.classList.add(name);
        } else {
            el.className += ' ' + name;
        }

        return _.isArray(className) ? this.addClass(el, className.slice(1)) : this;
    }



    /**
     * toggle class to element
     */
    toggleClass(el: Element, className: string) : DOM {
        if (!_.isElement(el)) {
            return this;
        }

        if (!className) {
            return this;
        }

        el.classList.toggle(className);

        return this;
    }


    /**
     * if element already has the class
     */
    hasClass(el: Element, className: string) : boolean {
        if (!_.isElement(el)) {
            throw new Error(`el argument is required! el = ${el}; className = ${className}`);
        }

        if (!className) {
            throw new Error(`className argument is required! el = ${el}; className = ${className}`);
        }

        return el.classList.contains(className);
    }


    /**
     * Add attribute to the element.
     */
    addAttribute(el: Element, attribute: string = '', value: string = ''): DOM {
        if (!_.isElement(el)) {
            return this;
        }
        el.setAttribute(attribute, value);

        return this;
    }


    /**
     * Add Style to the element.
     */
    addStyle(el: HTMLElement, key: string, value: string): DOM {
        if (!_.isElement(el)) {
            return this;
        }
        el.style[key] = value;

        return this;
    }


    /**
     * Remove Style from the element.
     */
    removeStyle(el: HTMLElement, key: string): DOM {
        if (!_.isElement(el)) {
            return this;
        }
        el.style.removeProperty(key);

        return this;
    }



    /**
     * Get current element dimensions
     */
    getDimensions(el: HTMLElement): ElementDimensions {
        if (!_.isElement(el)) {
            throw new Error('el argument must be an HTMLElement');
        }

        let style = this.getCurrentStyle(el);
        let horizontMargin = parseFloat(style.marginLeft) + parseFloat(style.marginRight);
        let verticalMargin = parseFloat(style.marginBottom) + parseFloat(style.marginTop);

        return {
            width: el.offsetWidth,
            height: el.offsetHeight,
            horizontMargin: horizontMargin,
            verticalMargin: verticalMargin,
            horizontPadding: parseFloat(style.paddingLeft) + parseFloat(style.paddingRight),
            verticalPadding: parseFloat(style.paddingBottom) + parseFloat(style.paddingTop),
            horizontBorder: parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth),
            verticalBorder: parseFloat(style.borderBottomWidth) + parseFloat(style.borderTopWidth),
            fullWidth: el.offsetWidth + horizontMargin,
            fullHeight: el.offsetHeight + verticalMargin,
            marginRight: parseFloat(style.marginRight),
            marginLeft: parseFloat(style.marginLeft),
            marginTop: parseFloat(style.marginTop),
            marginBottom: parseFloat(style.marginBottom)
        };
    }

    setElementSize(element: HTMLElement, size: ElementSize, unit: string = 'px') : void {
        element.style.width = size.width + unit;
        element.style.height = size.height + unit;
    }

    getCurrentStyle(el: HTMLElement) : CSSStyleDeclaration {
        if (!_.isElement(el)) {
            throw new Error('el argument must be an HTMLElement');
        }
        return window.getComputedStyle(el) || el['currentStyle'];
    }


    /**
     * Hide element from dom.
     */
    hide(el: Element): DOM {
        this.removeClass(el, 'show');
        this.addClass(el, 'hide');
        return this;
    }


    /**
     * Get element to be invisible
     * @param el
     * @returns {DOM}
     */
    invisible(el: HTMLElement): DOM {
        this.removeClass(el, 'cgp-visible');
        this.addClass(el, 'cgp-invisible');
        return this;
    }


    /**
     * Show element on dom
     * @param el
     */
    show(el: Element): DOM {
        this.removeClass(el, 'hide');
        this.addClass(el, 'show');
        return this;
    }

    /**
     * Get element to be visible
     * @param el
     * @returns {DOM}
     */
    visible(el: HTMLElement): DOM {
        this.removeClass(el, 'cgp-invisible');
        this.addClass(el, 'cgp-visible');
        return this;
    }


    /**
     * Remove class from element.
     * @param el
     * @param className
     */
    removeClass(el: Element, className: string): DOM {
        if (!_.isElement(el)) {
            throw new Error('el argument must be an element');
        }

        el.classList.remove(className);
        return this;
    }


    removeAttribute(el: Element, attr: string = ''): DOM {
        if (!_.isElement(el)) {
            throw new Error('el argument must be an element');
        }

        el.removeAttribute(attr);
        return this;
    }

    isInputElement(tagName: string = ''): boolean {
        return 'INPUT' === tagName;
    }


    /**
     * Sets the innerHTML for the provided element
     */
    html(html: string, el: Element): DOM {
        if (_.isNullOrUndefined(html)) {
            this.logger.error('Html is not valid parameter');
            return this;
        }
        el.innerHTML = html;
        return this;
    }


    /**
     * Clear elements from his node childs nor content.
     */
    clear(target: Element): DOM {
        while (target.firstChild) {
            target.removeChild(target.firstChild);
        }

        return this;
    }


    camelCase(str: string): string {
        return str.replace(/-([a-z])/g, (g) => {
            return g[1].toUpperCase();
        });
    }


    isTag(str: string): boolean {
        return 0 === str.search(/^<\w+>$/);
    }

    isId(str: string ) {
        return 0 === str.search(/^#\w+$/);
    }

    parseDataAttributes(el: Element): object {
        let data = {};

        [].forEach.call(el.attributes, (attr) => {
            if (/^data-/.test(attr.name)) {
                let camelCaseName = attr.name.substr(5).replace(/-(.)/g, ($0, $1) => {
                    return $1.toUpperCase();
                });
                data[camelCaseName] = attr.value;
            }
        });

        return data;
    }

    text(el: Element, value: string): DOM {
        if (!_.isElement(el)) {
            throw new Error('el argument must be a HTMLElement');
        }
        el.textContent = value;
        return this;
    }

    /**
     * wrapper for add event listeners on DOM, returns a self removing method.
     */
    addEventListener(el: Element | Document, type: string, callback: EventListenerOrEventListenerObject, useCapture: boolean = false) : DomEventSubscription {

        el.addEventListener(type, callback, useCapture);

        return {
            remove: function(msgElement: Element | Document, msgType: string, msgCallback: EventListenerOrEventListenerObject) {
                return () => {
                    return msgElement.removeEventListener(msgType, msgCallback);
                };
            }(el, type, callback)
        };
    }




    /**
     *
     *
     * @description -  Generic event handler, all the component should subscribe to one event function,
     * They should pass the element it self define specific id inside the element,
     * Event type, And the event callback.
     *
     * @usage:
     *           onDOMEvent(DOMElement, '#testid', 'click', ()=> {alert('onClick happend'});
     *           onDOMEvent(DOMElement, 'click', ()=> {alert('onClick happend'});
     */
    onDOMElementEvent(element: Element, eventName: string, callback: EventListenerOrEventListenerObject, useCapture: boolean = false) : DomEventSubscription {

        if (!_.isElement(element)) {
            throw new Error('element argument must be of type HTMLElement');
        }

        if(!_.isString(eventName)) {
            throw new Error('eventName argument must be of type String');
        }

        if(!_.isFunction(callback)) {
            throw new Error('callback argument must be of type Function');
        }

        element.addEventListener(eventName, callback, useCapture);

        return {
            remove: () => element.removeEventListener(eventName, callback)
        };
    }


    setInterval(callback: () => void, timeout: number, name?: TimeIntervalName) : number {
        return namedSetInterval(name, callback, timeout);
    }


    clearInterval(handle: number): void {
        clearInterval(handle);
    }


    setTimeout(callback: () => void, timeout: number, name?: TimeIntervalName): number {
        return namedSetTimeout(name, callback, timeout);
    }

    clearTimeout(handle: number): void {
        clearTimeout(handle);
    }
}

export default new DOM();
